import type { Root, RootContent } from "mdast";
import { visit } from "unist-util-visit";
import {
    extractVimeoVideoId,
    extractYouTubeVideoId,
    normalizeApplePodcastEmbedUrl,
} from "./blogPostPreview";

/**
 * Repair legacy directive markdown saved before the custom TipTap node
 * existed. Two fixes:
 *   1. Turndown was escaping `_` and `*` in the URL — strip those backslashes
 *      so remark-directive reads a valid URL.
 *   2. Turndown collapsed the newline between the opening and closing fences
 *      to a space (e.g. `:::youtube{url="…"} :::`). remark-directive requires
 *      the fences on separate lines; re-insert the newline.
 *
 * Safe to run on correctly-formatted directives; it's a no-op.
 */
export function normalizeBlogPostEmbedMarkdown(md: string): string {
    // 1. Unescape backslash-escaped metacharacters inside the directive
    //    attribute block. Permissive `[^}]*` so additional attributes
    //    (e.g. apple-podcast's `kind="show"`) are preserved.
    const unescaped = md.replace(/:::(?:youtube|vimeo|video|apple-podcast)\{[^}]*\}/g, (match) =>
        match.replace(/\\([_*`])/g, "$1"),
    );
    // 2. Normalize the fence separator: any whitespace (incl. none) between
    //    the closing `}` and the `:::` closer becomes a real newline.
    return unescaped.replace(
        /(:::(?:youtube|vimeo|video|apple-podcast)\{[^}]+\})[ \t]+:::/g,
        "$1\n:::",
    );
}

/**
 * Remark plugin that converts the three whitelisted container directives —
 * `:::youtube{url="…"}`, `:::vimeo{url="…"}`, `:::video{url="…"}` — into
 * mdast hast nodes the react-markdown pipeline can render as iframes / video
 * tags. Unknown directive names fall through untouched so authors can't
 * inject arbitrary embeds, and malformed URLs become a visible placeholder
 * rather than a broken iframe.
 */
export function remarkBlogPostEmbeds() {
    return (tree: Root) => {
        visit(tree, (node) => {
            const n = node as unknown as {
                type: string;
                name?: string;
                attributes?: Record<string, string | null | undefined>;
                data?: Record<string, unknown>;
                children?: RootContent[];
            };
            if (
                n.type !== "containerDirective" &&
                n.type !== "leafDirective" &&
                n.type !== "textDirective"
            ) {
                return;
            }
            const name = n.name;
            if (
                name !== "youtube" &&
                name !== "vimeo" &&
                name !== "video" &&
                name !== "apple-podcast"
            ) {
                return;
            }
            const url = (n.attributes?.url ?? "").trim();
            if (!url) {
                n.data = {
                    ...(n.data ?? {}),
                    hName: "div",
                    hProperties: {
                        className: ["blog-embed", "blog-embed-error"],
                    },
                };
                n.children = [
                    {
                        type: "text",
                        value: `Missing URL for :::${name}`,
                    } as unknown as RootContent,
                ];
                return;
            }

            if (name === "youtube") {
                const videoId = extractYouTubeVideoId(url);
                if (!videoId) {
                    n.data = {
                        hName: "div",
                        hProperties: { className: ["blog-embed", "blog-embed-error"] },
                    };
                    n.children = [
                        {
                            type: "text",
                            value: `Unrecognized YouTube URL: ${url}`,
                        } as unknown as RootContent,
                    ];
                    return;
                }
                n.data = {
                    hName: "iframe",
                    hProperties: {
                        className: ["blog-embed", "blog-embed-youtube"],
                        src: `https://www.youtube.com/embed/${videoId}`,
                        frameBorder: 0,
                        allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
                        allowFullScreen: true,
                        // Without this, a stricter document-level Referrer-Policy
                        // (no-referrer / same-origin) leaves YouTube with no Referer
                        // header, triggering Error 153 even for normally-embeddable
                        // videos.
                        referrerPolicy: "strict-origin-when-cross-origin",
                        title: "YouTube video",
                        loading: "lazy",
                    },
                };
                n.children = [];
                return;
            }

            if (name === "vimeo") {
                const videoId = extractVimeoVideoId(url);
                if (!videoId) {
                    n.data = {
                        hName: "div",
                        hProperties: { className: ["blog-embed", "blog-embed-error"] },
                    };
                    n.children = [
                        {
                            type: "text",
                            value: `Unrecognized Vimeo URL: ${url}`,
                        } as unknown as RootContent,
                    ];
                    return;
                }
                n.data = {
                    hName: "iframe",
                    hProperties: {
                        className: ["blog-embed", "blog-embed-vimeo"],
                        src: `https://player.vimeo.com/video/${videoId}`,
                        frameBorder: 0,
                        allow: "autoplay; fullscreen; picture-in-picture",
                        allowFullScreen: true,
                        referrerPolicy: "strict-origin-when-cross-origin",
                        title: "Vimeo video",
                        loading: "lazy",
                    },
                };
                n.children = [];
                return;
            }

            if (name === "apple-podcast") {
                const embedUrl = normalizeApplePodcastEmbedUrl(url);
                if (!embedUrl) {
                    n.data = {
                        hName: "div",
                        hProperties: { className: ["blog-embed", "blog-embed-error"] },
                    };
                    n.children = [
                        {
                            type: "text",
                            value: `Unrecognized Apple Podcasts URL: ${url}`,
                        } as unknown as RootContent,
                    ];
                    return;
                }
                // `kind` selects the player size: "episode" (175px, single
                // episode card — Apple's default) or "show" (450px, full
                // podcast feed). Defaults to episode when omitted.
                const kindAttr = (n.attributes?.kind ?? "").toLowerCase();
                const isShow = kindAttr === "show";
                n.data = {
                    hName: "iframe",
                    hProperties: {
                        className: [
                            "blog-embed",
                            "blog-embed-apple-podcast",
                            isShow
                                ? "blog-embed-apple-podcast-show"
                                : "blog-embed-apple-podcast-episode",
                        ],
                        src: embedUrl,
                        frameBorder: 0,
                        height: isShow ? 450 : 175,
                        allow: "autoplay *; encrypted-media *; fullscreen *; clipboard-write",
                        sandbox:
                            "allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation",
                        referrerPolicy: "strict-origin-when-cross-origin",
                        title: "Apple Podcasts player",
                        loading: "lazy",
                    },
                };
                n.children = [];
                return;
            }

            // name === "video" — direct hosted URL
            n.data = {
                hName: "video",
                hProperties: {
                    className: ["blog-embed", "blog-embed-video"],
                    src: url,
                    controls: true,
                    preload: "metadata",
                },
            };
            n.children = [];
        });
    };
}
